import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/common/SiteHeader';
import GalleryCard from '../components/common/GalleryCard';
import galleryService from '../services/galleryService';

function Dashboard() {
	const [galleries, setGalleries] = useState([]);
	const [sharedGalleries, setSharedGalleries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		loadGalleries();
	}, []);

	const loadGalleries = async () => {
		try {
			setLoading(true);
			const [galleriesData, sharedGalleriesData] = await Promise.all([
				galleryService.getUserGalleries(),
				galleryService.getSharedGalleries()
			]);
			setGalleries(galleriesData);
			setSharedGalleries(sharedGalleriesData);
			setError(null);
		} catch (err) {
			setError('Failed to load galleries. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
				<SiteHeader />
				<div className="p-8 max-w-6xl mx-auto w-full">
					<div className="text-center">
						<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
						<p className="mt-4 text-gray-600">Loading your galleries...</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
			<SiteHeader />
			<div className="p-4 md:p-8 max-w-6xl mx-auto w-full">
				<div className="flex justify-between items-center mb-6">
					<h2 className="text-2xl font-bold">My galleries</h2>
					<Link to="/new-gallery">
						<button className="px-3 py-2 md:px-4 md:py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors flex items-center gap-1.5 text-sm md:text-base">
							<svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
							</svg>
							<span className="hidden sm:inline">Create New Gallery</span>
							<span className="sm:hidden">New</span>
						</button>
					</Link>
				</div>

				{error && (
					<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
						{error}
						<button
							onClick={loadGalleries}
							className="ml-2 underline hover:no-underline"
						>
							Try again
						</button>
					</div>
				)}

				<div className="grid gap-4 mb-8">
					{galleries.length === 0 ? (
						<div className="text-center py-12">
							<h3 className="text-xl text-gray-600 mb-4">No galleries yet</h3>
							<p className="text-gray-500 mb-6">Create your first gallery to get started!</p>
						</div>
					) : (
						galleries.map((gallery) => (
							<GalleryCard key={gallery.id} gallery={gallery} />
						))
					)}
				</div>

				{sharedGalleries.length > 0 && (
					<div className="mb-8">
						<h2 className="text-2xl font-bold mb-4">Galleries shared with me</h2>
						<div className="grid gap-4">
							{sharedGalleries.map((gallery) => (
								<GalleryCard key={gallery.id} gallery={gallery} isShared={true} />
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default Dashboard;
